package com.fireavert.reports_page.reports_page.client

import com.fireavert.common.Try
import com.fireavert.events.active_events.data_models.responses.GetActiveEventsForAllPropertiesResponse
import com.fireavert.events.active_events.data_models.responses.GetActiveEventsForPropertyResponse
import com.fireavert.reports_page.reports_page.data_models.requests.DeviceToEventRatioRequest
import com.fireavert.reports_page.reports_page.data_models.requests.HighRiskAverageEventRequest
import com.fireavert.reports_page.reports_page.data_models.requests.HighRiskTenantRequest
import com.fireavert.reports_page.reports_page.data_models.requests.ReportsEventsRequest
import com.fireavert.reports_page.reports_data_export.data_models.requests.ReportsExportDialogRequest
import com.fireavert.reports_page.reports_page.data_models.requests.YearlyReportRequest
import com.fireavert.reports_page.reports_page.data_models.responses.DeviceToEventRatioResponse
import com.fireavert.reports_page.reports_page.data_models.responses.HighRiskTenantsResponse
import com.fireavert.reports_page.reports_page.data_models.responses.ReportYearlyDataResponse
import com.fireavert.reports_page.reports_page.data_models.responses.ReportsEventResponse
import com.fireavert.reports_page.reports_data_export.data_models.responses.ReportsExportDialogResponse
import com.fireavert.reports_page.reports_page.data_models.responses.ReportsFireClaimReportResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.request.url
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.appendPathSegments
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.http.takeFrom

class ReportsPageRepositoryDataSourceImpl(
    private val baseUrl: String,
    private val httpClient: HttpClient,
) : ReportsPageRepositoryDataSource {
    override suspend fun getReportsSectionData(
        request: ReportsEventsRequest
    ): Try<ReportsEventResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer ${request.token}")
                url {
                    takeFrom(baseUrl)
                    appendPathSegments("allTimeReport")
                    parameters.append("type", request.type)
                    parameters.append("timeZone", request.timeZone)
                    request.propertyId?.let { parameters.append("propertyId", it.toString()) }
                }
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun getYearlyEvents(request: YearlyReportRequest): Try<ReportYearlyDataResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer ${request.token}")
                url {
                    takeFrom(baseUrl)
                    appendPathSegments("reports", "yearlyReport")
                    parameters.append("year", request.yearToGet.toString())
                    parameters.append("timeZone", request.timeZone)
                    request.propertyId?.let { parameters.append("propertyId", it.toString()) }
                }
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun getHighRiskTenants(
        request: HighRiskTenantRequest,
        token: String
    ): Try<HighRiskTenantsResponse> {
        return Try.Companion.fromCoCallable {
            try {
                val response = httpClient.post {
                    header(HttpHeaders.Authorization, "Bearer $token")
                    url {
                        takeFrom(baseUrl)
                        appendPathSegments("reports", "highRiskTenants")
                    }
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
                if (response.status.isSuccess()) {
                    response.body<HighRiskTenantsResponse>()
                } else {
                    throw ClientRequestException(response, "Request failed with status ${response.status}")
                }
            } catch (e: Exception) {
                throw e
            }
        }
    }


    override suspend fun getAllPropertiesAverageValue(request: HighRiskAverageEventRequest, token: String): Try<Double> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.post {
                header(HttpHeaders.Authorization, "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(request)
                url {
                    takeFrom(baseUrl)
                    appendPathSegments("reports", "averagePropertyEvent")
                }
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun getPropertyEventsOverTime(
        propertyId: Int?,
        token: String
    ): Try<ReportsFireClaimReportResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer $token")
                url {
                    takeFrom(baseUrl)
                    appendPathSegments("reports", "fireClaimReportData")
                    propertyId?.let { parameters.append("id", it.toString()) }
                }
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun exportReportEventData(request: ReportsExportDialogRequest, token: String): Try<ReportsExportDialogResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.post {
                header(HttpHeaders.Authorization, "Bearer $token")
                url {
                    takeFrom(baseUrl)
                    appendPathSegments("reports", "export")
                }
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun exportEventsHappeningNowAll(token: String): Try<GetActiveEventsForAllPropertiesResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer $token")
                url("$baseUrl/activeEventsForAllProperties")
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun exportEventsHappeningNow(
        propertyId: Int?,
        token: String,
    ): Try<GetActiveEventsForPropertyResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer $token")
                url("$baseUrl/activeEventsForProperty/$propertyId")
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun getDeviceToEventRatio(
        request: DeviceToEventRatioRequest,
        token: String
    ): Try<DeviceToEventRatioResponse> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.post {
                header(HttpHeaders.Authorization, "Bearer $token")
                url("$baseUrl/reports/deviceToEventRatio")
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }

    override suspend fun getFiresAvertedCount(propertyId: Int?, token: String): Try<Int> {
        return Try.Companion.fromCoCallable {
            val response = httpClient.get {
                header(HttpHeaders.Authorization, "Bearer $token")
                url("$baseUrl/reports/firesAverted/$propertyId")
            }
            if (response.status.isSuccess()) {
                response.body()
            } else {
                throw ClientRequestException(response, "Request failed with status ${response.status}")
            }
        }
    }
}